import { useMemo } from 'react';
import { User as UserIcon, Bot } from 'lucide-react';
import { PageBody } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useTenant } from './context';
import { relativeTime } from '@/lib/format';

interface Evt {
  id: string;
  ts: string;
  actor: string;
  kind: 'staff' | 'system';
  action: string;
  detail: string;
  result: 'success' | 'failed';
}

export function TenantEvents() {
  const { audit } = useTenant();

  const events = useMemo<Evt[]>(() => {
    return audit
      .map(a => ({
        id: a.id,
        ts: a.timestamp,
        actor: a.staffName,
        kind: (a.staffName === 'system' || a.staffEmail === '—' ? 'system' : 'staff') as 'staff' | 'system',
        action: a.action,
        detail: a.target + (a.reason ? ` — ${a.reason}` : ''),
        result: a.result,
      }))
      .sort((x, y) => y.ts.localeCompare(x.ts));
  }, [audit]);

  return (
    <PageBody>
      <Card>
        <div className="px-5 py-3 border-b border-border flex items-center gap-3 text-xs">
          <span className="text-fg font-semibold">{events.length} events</span>
          <span className="text-fg-subtle">· Operator actions + system events from /admin/v1/audit</span>
        </div>
        {events.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-fg-muted">
            No events recorded for this customer yet.
          </div>
        ) : (
          <ol className="relative px-5 py-4">
            <span className="absolute left-[34px] top-4 bottom-4 w-px bg-border" />
            {events.map(e => (
              <li key={e.id} className="relative pl-10 pb-5 last:pb-0">
                <span className="absolute left-3.5 top-0.5 w-6 h-6 rounded-full bg-bg-elevated border border-border flex items-center justify-center text-fg-muted">
                  {e.kind === 'staff' ? <UserIcon size={11} /> : <Bot size={11} />}
                </span>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-semibold text-fg text-sm">{e.actor}</span>
                  <Badge tone={e.kind === 'staff' ? 'neutral' : 'accent'}>{e.kind}</Badge>
                  <span className="font-mono text-xs text-fg">{e.action}</span>
                  <Badge tone={e.result === 'success' ? 'success' : 'danger'}>{e.result}</Badge>
                  <span className="ml-auto text-2xs text-fg-subtle">{relativeTime(e.ts)}</span>
                </div>
                <div className="text-xs text-fg-muted mt-0.5">{e.detail}</div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </PageBody>
  );
}
